import type { User } from '@/types/auth';
import type { OrganizationKey } from '@/types/user-admin';
import { ENABLED_WEEKEND_DISCHARGE_HOSPITALS } from '@/constant';
import { PERMISSIONS, STATIC_SUBSCRIBER_PERMISSIONS, hasAnyPermission, hasPermission } from '@/utils/permissions';
export const ADMIN_ROLE_KEY = 'ADMIN';
export const SUPERVISOR_ROLE_KEY = 'SUPERVISOR';
export const STAFF_ROLE_KEY = 'STAFF';
export const ADMIN_HOME_PATH = '/admin/hospitals';
export const SUPERVISOR_HOME_PATH = '/supervisor/scorecard';
export const OTHER_THAN_ADMIN_HOME_PATH_1 = '/dashboard';
export const OTHER_THAN_ADMIN_HOME_PATH_2 = '/patients';
export const NO_ACCESS_PATH = '/no-access';
export const ORGANIZATION_KEYS: Record<string, OrganizationKey> = {
    SILVERSTAY: 'SILVERSTAY',
    HOSPITAL: 'HOSPITAL'
};

const hasRoleKey = (user: User | null | undefined, roleKey: string): boolean => {
    if (!user?.role) return false;
    const normalized = user.role.key?.toUpperCase();
    if (!normalized) return false;
    return normalized === roleKey.toUpperCase();
};

export const isAdmin = (user: User | null | undefined): boolean => hasRoleKey(user, ADMIN_ROLE_KEY);

export const isAdminRoute = (pathname: string): boolean => pathname.startsWith('/admin') || pathname.startsWith('/activity-logs');

/** Resolve organization for the logged-in user (`GET /users/me`). */
export const resolveUserOrganizationKey = (user: User | null | undefined): OrganizationKey => {
    if (!user) {
        return ORGANIZATION_KEYS.HOSPITAL;
    }
    if (user.organization?.key === ORGANIZATION_KEYS.SILVERSTAY || user.organization?.key === ORGANIZATION_KEYS.HOSPITAL) {
        return user.organization?.key;
    }
    return ORGANIZATION_KEYS.HOSPITAL;
};

export const isSilverStayUser = (user: User | null | undefined): boolean =>
    resolveUserOrganizationKey(user) === ORGANIZATION_KEYS.SILVERSTAY;

export const isSupervisor = (user: User | null | undefined): boolean => hasRoleKey(user, SUPERVISOR_ROLE_KEY);

export const isStaff = (user: User | null | undefined): boolean => hasRoleKey(user, STAFF_ROLE_KEY);

export const isStaffOrSupervisor = (user: User | null | undefined): boolean => isStaff(user) || isSupervisor(user);

/** Sticky Notes column: Admin, or Hospital supervisor (not SilverStay supervisor/staff). */
export const canViewStickyNoteColumn = (user: User | null | undefined): boolean =>
    isAdmin(user) || (isSupervisor(user) && !isSilverStayUser(user));

/** LLOS note edit: Admin, hospital staff (navigators), or hospital supervisor — not SilverStay supervisors. */
export const canEditLlosNote = (user: User | null | undefined): boolean =>
    isAdmin(user) || isStaff(user) || (isSupervisor(user) && !isSilverStayUser(user));

/** In-app notifications and Socket.IO are enabled for every signed-in role. */
export const shouldEnableNotifications = (user: User | null | undefined): boolean => Boolean(user);

type HomeCandidate = {
    path: string;
    permission?: string | readonly string[];
    when?: (user: User | null | undefined) => boolean;
};

const hasWeekendDischargeHospital = (user: User | null | undefined): boolean => {
    const defaultHospitalName =
        user?.hospitals
            ?.find(({ isDefault }) => isDefault)
            ?.name?.trim()
            ?.toLowerCase() ?? '';

    return ENABLED_WEEKEND_DISCHARGE_HOSPITALS.includes(defaultHospitalName);
};

/** Ordered pages a signed-in user can land on. Role preference is applied first in `getUserHomePath`. */
const ACCESSIBLE_HOMES: HomeCandidate[] = [
    {
        path: OTHER_THAN_ADMIN_HOME_PATH_1,
        permission: PERMISSIONS.WEEKEND_DISCHARGE_VIEW,
        when: hasWeekendDischargeHospital
    },
    { path: OTHER_THAN_ADMIN_HOME_PATH_2, permission: PERMISSIONS.CENSUS_VIEW },
    { path: '/my-patients', permission: PERMISSIONS.CCM_VIEW },
    { path: '/meeting-view', permission: PERMISSIONS.MEETING_VIEW_VIEW },
    { path: '/insights', permission: PERMISSIONS.INSIGHTS_VIEW },
    { path: '/dart-report', permission: PERMISSIONS.DART_REPORT_VIEW },
    { path: SUPERVISOR_HOME_PATH, permission: PERMISSIONS.SCORECARD_VIEW },
    { path: '/static-subscribers', permission: STATIC_SUBSCRIBER_PERMISSIONS },
    { path: '/meeting-transcription', permission: PERMISSIONS.MEETING_TRANSCRIPTION_MANAGE },
    { path: '/manage-staff', permission: PERMISSIONS.STAFF_MANAGE },
    { path: '/acl', permission: PERMISSIONS.ACL_MANAGE }
];

const canOpenHome = (
    user: User | null | undefined,
    permissions: readonly string[] | null | undefined,
    candidate: HomeCandidate
): boolean => {
    if (candidate.when && !candidate.when(user)) {
        return false;
    }
    if (!candidate.permission) {
        return true;
    }
    return typeof candidate.permission === 'string'
        ? hasPermission(permissions, candidate.permission)
        : hasAnyPermission(permissions, candidate.permission);
};

const preferredHomeCandidates = (user: User | null | undefined): HomeCandidate[] => {
    if (isAdmin(user)) {
        return [{ path: ADMIN_HOME_PATH }];
    }
    if (isSupervisor(user)) {
        return isSilverStayUser(user)
            ? [{ path: OTHER_THAN_ADMIN_HOME_PATH_2, permission: PERMISSIONS.CENSUS_VIEW }]
            : [{ path: SUPERVISOR_HOME_PATH, permission: PERMISSIONS.SCORECARD_VIEW }];
    }
    if (hasWeekendDischargeHospital(user)) {
        return [
            {
                path: OTHER_THAN_ADMIN_HOME_PATH_1,
                permission: PERMISSIONS.WEEKEND_DISCHARGE_VIEW,
                when: hasWeekendDischargeHospital
            }
        ];
    }
    return [{ path: OTHER_THAN_ADMIN_HOME_PATH_2, permission: PERMISSIONS.CENSUS_VIEW }];
};

/** First path this user can open. Role still picks the preference; permissions decide whether it is usable. */
export const getUserHomePath = (user: User | null | undefined, permissions: readonly string[] | null | undefined): string => {
    const preferred = preferredHomeCandidates(user);
    const ordered = [...preferred, ...ACCESSIBLE_HOMES.filter((candidate) => !preferred.some((item) => item.path === candidate.path))];
    return ordered.find((candidate) => canOpenHome(user, permissions, candidate))?.path ?? NO_ACCESS_PATH;
};
